import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../api';

const emptyPw = { current: '', next: '', confirm: '' };

// Shared "change my own password at any time" flow — used by both TopBar
// (MaterialWatch) and Portal.jsx (Portal home), so every user has access to
// it regardless of which modules they're assigned.
export default function ChangePasswordModal({ show, onClose }) {
  const { user } = useAuth();

  const [pwForm, setPwForm]             = useState(emptyPw);
  const [pwErrors, setPwErrors]         = useState({});
  const [pwSubmitError, setPwSubmitError] = useState('');
  const [pwSuccess, setPwSuccess]       = useState(false);
  const [pwLoading, setPwLoading]       = useState(false);

  useEffect(() => {
    if (show) {
      setPwForm(emptyPw);
      setPwErrors({});
      setPwSubmitError('');
      setPwSuccess(false);
    }
  }, [show]);

  if (!show) return null;

  const validatePw = (form) => {
    const errs = {};
    if (!form.current)   errs.current  = 'Current password is required';
    if (!form.next)      errs.next     = 'New password is required';
    else if (form.next.length < 6)
                         errs.next     = 'New password must be at least 6 characters';
    if (!form.confirm)   errs.confirm  = 'Please confirm your new password';
    else if (form.next && form.next !== form.confirm)
                         errs.confirm  = 'Passwords do not match';
    if (form.current && form.next && form.current === form.next)
                         errs.next     = 'New password must differ from current password';
    return errs;
  };

  const handlePwChange = (field) => (e) => {
    const val = e.target.value;
    setPwForm((prev) => ({ ...prev, [field]: val }));
    if (pwErrors[field]) setPwErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    if (pwSubmitError)   setPwSubmitError('');
  };

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    const errs = validatePw(pwForm);
    if (Object.keys(errs).length) { setPwErrors(errs); return; }

    setPwLoading(true);
    setPwSubmitError('');
    try {
      await authApi.changePassword({ currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change password';
      if (msg.toLowerCase().includes('current')) {
        setPwErrors({ current: 'Current password is incorrect' });
      } else {
        setPwSubmitError(msg);
      }
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                 style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
              <svg viewBox="0 0 20 20" fill="white" className="w-4.5 h-4.5 w-5 h-5">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Change Password</h3>
              <p className="text-xs text-gray-400 mt-0.5">{user?.fullName || user?.username}</p>
            </div>
          </div>
          {!pwLoading && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {pwSuccess ? (
            /* ── Success state ── */
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-8 h-8 text-green-600">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="text-base font-semibold text-gray-900">Password Changed</p>
                <p className="text-sm text-gray-500 mt-1">Your password has been updated successfully.</p>
              </div>
              <button
                type="button"
                className="mt-2 px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition-colors"
                onClick={onClose}
              >
                OK
              </button>
            </div>
          ) : (
            /* ── Form state ── */
            <form onSubmit={handlePwSubmit} className="space-y-4" noValidate>
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={pwForm.current}
                  onChange={handlePwChange('current')}
                  className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors ${
                    pwErrors.current ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  disabled={pwLoading}
                />
                {pwErrors.current && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠</span> {pwErrors.current}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={pwForm.next}
                  onChange={handlePwChange('next')}
                  className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors ${
                    pwErrors.next ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  disabled={pwLoading}
                />
                {pwErrors.next && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠</span> {pwErrors.next}
                  </p>
                )}
              </div>

              {/* Confirm New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={pwForm.confirm}
                  onChange={handlePwChange('confirm')}
                  className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors ${
                    pwErrors.confirm ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  disabled={pwLoading}
                />
                {pwErrors.confirm && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠</span> {pwErrors.confirm}
                  </p>
                )}
              </div>

              {/* Server-level error */}
              {pwSubmitError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                  {pwSubmitError}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={pwLoading}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={pwLoading}
                  className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center gap-2"
                >
                  {pwLoading && (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                  )}
                  {pwLoading ? 'Saving…' : 'Change Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
